import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Tooltip, Typography, Stack, SxProps } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';

const MultiLineOverflow = styled(Typography).attrs({
  component: 'span',
})<{
  $bold?: boolean;
  $maxLines: number;
}>`
  overflow: hidden;
  word-break: break-word;
  font-size: inherit;
  font-weight: ${({ $bold, theme }) =>
    $bold ? theme.typography.fontWeightBold : 400};
  ${({ $maxLines }) =>
    $maxLines > 1 &&
    `
   display: -webkit-box;
    -webkit-line-clamp: ${$maxLines};
    line-clamp: ${$maxLines};
     -webkit-box-orient: vertical;
  `};
`;

type Props = {
  children: React.ReactNode;
  className?: string;
  variant?: Variant;
  color?: any;
  bold?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  autoTooltipPlacement?: boolean;
  placement?: 'top' | 'left' | 'right' | 'bottom';
  anchorRef?: any;
  onClick?: () => void;
  sx?: SxProps;
  popperSx?: SxProps;
  typographySx?: SxProps;
  tooltipContent?: string;
  forceTooltip?: boolean;
  maxLines?: number;
  'aria-label'?: string;
};

const Wrapper = styled(Tooltip)`
  overflow: hidden;
  cursor: inherit;
  width: 100%;
`;

const OverflowTooltip = ({
  children,
  className,
  variant,
  color,
  bold,
  icon,
  autoTooltipPlacement,
  placement = 'top',
  anchorRef,
  onClick,
  sx,
  popperSx,
  typographySx,
  tooltipContent,
  forceTooltip,
  maxLines = 1,
  'aria-label': ariaLabel,
}: Props) => {
  const textElementRef = useRef<HTMLSpanElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const compareSize = useCallback(() => {
    const compare =
      (textElementRef.current?.scrollWidth || 0) >
        (textElementRef.current?.clientWidth || 0) ||
      (textElementRef.current?.scrollHeight || 0) >
        (textElementRef.current?.clientHeight || 0);
    setIsOverflow(compare);
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => compareSize());
    const textElement = textElementRef?.current;

    if (textElement) {
      observer.observe(textElement);
    }

    compareSize();
    window.addEventListener('resize', compareSize);

    return () => {
      window.removeEventListener('resize', compareSize);
      if (textElement) {
        observer.unobserve(textElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper
      title={tooltipContent || children || false}
      disableHoverListener={!forceTooltip && !isOverflow}
      placement={autoTooltipPlacement ? undefined : placement}
      className={className}
      onClick={onClick}
      PopperProps={{
        sx: { whiteSpace: 'pre-wrap', ...popperSx },
        anchorEl: anchorRef?.current || textElementRef?.current,
      }}
      aria-label={ariaLabel}
    >
      <Stack direction="row" sx={{ alignItems: 'center', ...sx }}>
        <MultiLineOverflow
          ref={textElementRef}
          sx={typographySx}
          variant={variant}
          color={color}
          $bold={bold}
          $maxLines={maxLines}
          noWrap={maxLines === 1}
        >
          {children}
        </MultiLineOverflow>
        {icon && isOverflow && (
          <Stack sx={{ margin: '0 0 0 auto' }}>
            {React.createElement(icon)}
          </Stack>
        )}
      </Stack>
    </Wrapper>
  );
};

export default OverflowTooltip;
